import { useCallback, useEffect, useMemo, useState } from 'react';
import { MapPin, Microchip, Pencil, Plus, Trash2 } from 'lucide-react';
import { useParams } from 'react-router-dom';
import {
  createMachineImuInstallation,
  deleteMachineImuInstallation,
  getMachineImuInstallations,
  updateMachineImuInstallation,
} from '../api/api';
import { ActionDrawer, ConfirmDialog, Toast } from '../components/ui';
import styles from './ImuInstallation.module.scss';

function ImuInstallation() {
  const { machineId: machineIdParam } = useParams();
  const machineId = machineIdParam ? parseInt(machineIdParam, 10) : null;
  const [installations, setInstallations] = useState([]);
  const [editingInstallation, setEditingInstallation] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [formData, setFormData] = useState({ imuId: '', place: '', canPort: '0', permanent: true });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const fetchInstallations = useCallback(async () => {
    if (!machineId) {
      return;
    }
    try {
      setLoading(true);
      const response = await getMachineImuInstallations(machineId);
      setInstallations(response.data?.installations || []);
      setError('');
    } catch (requestError) {
      setError(requestError.response?.data?.detail || 'Failed to load IMU installations.');
    } finally {
      setLoading(false);
    }
  }, [machineId]);

  useEffect(() => {
    fetchInstallations();
  }, [fetchInstallations]);

  const closeDrawer = () => {
    if (saving) {
      return;
    }
    setDrawerOpen(false);
    setEditingInstallation(null);
    setFormData({ imuId: '', place: '', canPort: '0', permanent: true });
  };

  const openCreate = () => {
    setEditingInstallation(null);
    setFormData({ imuId: '', place: '', canPort: '0', permanent: true });
    setError('');
    setDrawerOpen(true);
  };

  const openEdit = installation => {
    setEditingInstallation(installation);
    setFormData({
      imuId: String(installation.imu_id),
      place: installation.place || '',
      canPort: String(installation.can_port ?? 0),
      permanent: installation.permanent ?? true,
    });
    setError('');
    setDrawerOpen(true);
  };

  const handleSubmit = async () => {
    const imuId = parseInt(formData.imuId, 10);
    if (Number.isNaN(imuId) || !formData.place.trim()) {
      setError('Enter a numeric IMU ID and installation place.');
      return;
    }
    try {
      setSaving(true);
      setError('');
      const payload = {
        imu_id: imuId,
        place: formData.place.trim(),
        can_port: parseInt(formData.canPort, 10),
        permanent: formData.permanent,
      };
      if (editingInstallation) {
        await updateMachineImuInstallation(machineId, editingInstallation.imu_id, payload);
        setSuccess(`IMU ${editingInstallation.imu_id} updated.`);
      } else {
        await createMachineImuInstallation(machineId, payload);
        setSuccess(`IMU ${imuId} installed.`);
      }
      setDrawerOpen(false);
      setEditingInstallation(null);
      setFormData({ imuId: '', place: '', canPort: '0', permanent: true });
      await fetchInstallations();
    } catch (requestError) {
      setError(requestError.response?.data?.detail || 'Failed to save IMU installation.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async installation => {
    try {
      setSaving(true);
      setError('');
      await deleteMachineImuInstallation(machineId, installation.imu_id);
      setSuccess(`IMU ${installation.imu_id} removed.`);
      setPendingDelete(null);
      await fetchInstallations();
    } catch (requestError) {
      setError(requestError.response?.data?.detail || 'Failed to delete IMU installation.');
    } finally {
      setSaving(false);
    }
  };

  const groupedInstallations = useMemo(() => {
    const ports = new Map();
    installations.forEach(installation => {
      const port = installation.can_port ?? 0;
      ports.set(port, [...(ports.get(port) || []), installation]);
    });
    return [...ports.entries()].sort(([left], [right]) => left - right);
  }, [installations]);

  const formatTimestamp = value => (value ? new Date(value).toLocaleString() : '—');

  return (
    <div className={styles.page}>
      <header className={styles.pageHeader}>
        <div>
          <span className={styles.eyebrow}>Service records</span>
          <h1>IMU installation</h1>
          <p>Document where each sensor is mounted and which CAN bus supplies it.</p>
        </div>
        <button type="button" className={styles.primaryButton} onClick={openCreate}>
          <Plus size={16} /> Install IMU
        </button>
      </header>
      {error && (
        <Toast tone="error" onClose={() => setError('')}>
          {error}
        </Toast>
      )}
      {success && (
        <Toast tone="success" onClose={() => setSuccess('')}>
          {success}
        </Toast>
      )}

      <section className={styles.summary}>
        <div>
          <span>Installed sensors</span>
          <strong>{installations.length}</strong>
        </div>
        <div>
          <span>Permanent mounts</span>
          <strong>{installations.filter(item => item.permanent).length}</strong>
        </div>
        <div>
          <span>CAN buses in use</span>
          <strong>{groupedInstallations.length}</strong>
        </div>
      </section>

      {loading ? (
        <div className={styles.loading}>Loading installation records…</div>
      ) : groupedInstallations.length ? (
        <div className={styles.portGrid}>
          {groupedInstallations.map(([port, portInstallations]) => (
            <section className={styles.portPanel} key={port}>
              <header>
                <div>
                  <span>CAN</span>
                  <strong>Port {port}</strong>
                </div>
                <b>
                  {portInstallations.length} sensor{portInstallations.length === 1 ? '' : 's'}
                </b>
              </header>
              <div className={styles.sensorList}>
                {portInstallations.map(installation => (
                  <article key={installation.imu_id}>
                    <span className={styles.sensorIcon}>
                      <Microchip size={17} />
                    </span>
                    <div className={styles.sensorIdentity}>
                      <strong>IMU {installation.imu_id}</strong>
                      <span>
                        <MapPin size={12} /> {installation.place}
                      </span>
                      <small>Updated {formatTimestamp(installation.updated_at)}</small>
                    </div>
                    <span
                      className={
                        installation.permanent ? styles.permanentBadge : styles.temporaryBadge
                      }
                    >
                      {installation.permanent ? 'Permanent' : 'Temporary'}
                    </span>
                    <div className={styles.actions}>
                      <button
                        type="button"
                        onClick={() => openEdit(installation)}
                        title="Edit installation"
                      >
                        <Pencil size={15} />
                      </button>
                      <button
                        type="button"
                        onClick={() => setPendingDelete(installation)}
                        title="Remove installation"
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </article>
                ))}
              </div>
            </section>
          ))}
        </div>
      ) : (
        <div className={styles.emptyState}>
          <Microchip size={27} />
          <strong>No IMUs documented</strong>
          <p>Add the first sensor installation for this machine.</p>
        </div>
      )}

      <ConfirmDialog
        open={!!pendingDelete}
        title={`Remove IMU ${pendingDelete?.imu_id}?`}
        description="The installation record will be removed from this machine."
        confirmLabel="Remove"
        busyLabel="Removing…"
        busy={saving}
        onConfirm={() => handleDelete(pendingDelete)}
        onCancel={() => setPendingDelete(null)}
      />

      <ActionDrawer
        open={drawerOpen}
        eyebrow="IMU installation"
        title={editingInstallation ? `Edit IMU ${editingInstallation.imu_id}` : 'Install an IMU'}
        description="Installation records are used to interpret sensor status by CAN port and machine location."
        onClose={closeDrawer}
        closeDisabled={saving}
        footer={
          <>
            <button type="button" className={styles.secondaryButton} onClick={closeDrawer}>
              Cancel
            </button>
            <button
              type="button"
              className={styles.primaryButton}
              onClick={handleSubmit}
              disabled={saving}
            >
              {saving ? 'Saving…' : editingInstallation ? 'Save installation' : 'Install IMU'}
            </button>
          </>
        }
      >
        <div className={styles.drawerForm}>
          <label>
            <span>IMU ID</span>
            <input
              type="number"
              value={formData.imuId}
              onChange={event =>
                setFormData(current => ({ ...current, imuId: event.target.value }))
              }
              autoFocus
            />
          </label>
          <label>
            <span>Installation place</span>
            <input
              value={formData.place}
              onChange={event =>
                setFormData(current => ({ ...current, place: event.target.value }))
              }
              placeholder="e.g. boom, cabin, rear axle"
            />
          </label>
          <label>
            <span>CAN port</span>
            <select
              value={formData.canPort}
              onChange={event =>
                setFormData(current => ({ ...current, canPort: event.target.value }))
              }
            >
              <option value="0">CAN 0</option>
              <option value="1">CAN 1</option>
              <option value="2">CAN 2</option>
            </select>
          </label>
          <label className={styles.toggleField}>
            <input
              type="checkbox"
              checked={formData.permanent}
              onChange={event =>
                setFormData(current => ({ ...current, permanent: event.target.checked }))
              }
            />
            <span>
              <strong>Permanent installation</strong>
              <small>Expected to remain attached during normal machine operation.</small>
            </span>
          </label>
        </div>
      </ActionDrawer>
    </div>
  );
}

export default ImuInstallation;
